
import { useState, useEffect } from 'react';
import { SignInForm } from '@/components/auth/SignInForm';
import { SignUpFormContainer } from '@/components/auth/SignUpFormContainer';
import { Button } from '@/components/ui/button';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const Auth = () => {
  const [showSignUp, setShowSignUp] = useState(false);
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setShowSignUp(true);
      localStorage.setItem('pendingSignup', JSON.stringify({
        referralCode: ref,
        redirectAfterPayment: '/auth'
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    // Check if email verification is in progress
    const checkVerifying = () => {
      // Check localStorage flag
      const verifyingFlag = localStorage.getItem('emailVerifying') === 'true';
      
      // Also check URL hash for verification tokens
      const hash = window.location.hash;
      const hasTokens = hash.includes('access_token') && hash.includes('refresh_token');
      
      // If tokens are in hash, set localStorage flag and show overlay immediately
      if (hasTokens && !verifyingFlag) {
        localStorage.setItem('emailVerifying', 'true');
      }
      
      setIsVerifying(verifyingFlag || hasTokens);
    };

    checkVerifying();

    // Listen for storage changes and hash changes
    window.addEventListener('storage', checkVerifying);
    window.addEventListener('hashchange', checkVerifying);
    
    // Also check on page focus (when app comes to foreground)
    window.addEventListener('focus', checkVerifying);
    
    return () => {
      window.removeEventListener('storage', checkVerifying);
      window.removeEventListener('hashchange', checkVerifying);
      window.removeEventListener('focus', checkVerifying);
    };
  }, []);

  // Monitor authentication state during verification
  useEffect(() => {
    if (!isVerifying) return;

    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setIsAuthenticated(true);
        // Auto-redirect after a short delay to show success state
        setTimeout(() => {
          localStorage.removeItem('emailVerifying');
          navigate('/');
        }, 1500);
      }
    };

    // Check immediately
    checkAuth();

    // Also subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && isVerifying) {
        setIsAuthenticated(true);
        // Auto-redirect after a short delay to show success state
        setTimeout(() => {
          localStorage.removeItem('emailVerifying');
          navigate('/');
        }, 1500);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isVerifying, navigate]);

  const handleContinue = async () => {
    // Wait for authentication to complete before redirecting
    if (isAuthenticated) {
      localStorage.removeItem('emailVerifying');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {isVerifying && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-2xl p-8 max-w-md w-full space-y-6 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-center">
              <img 
                src={theme === 'dark' ? "/lovable-uploads/logo_mindnote_white.png" : "/lovable-uploads/321e5adc-687d-4ac5-904e-7be9d91e5340.png"}
                alt="MindNote"
                className="h-16 w-auto object-contain"
              />
            </div>
            <div className="flex justify-center">
              <CheckCircle className="h-20 w-20 text-green-500 animate-pulse" />
            </div>
            <div className="text-center space-y-3">
              <h1 className="text-2xl font-bold text-foreground">Verification successful!</h1>
              <p className="text-muted-foreground text-lg">
                {isAuthenticated 
                  ? "You're logged in! Redirecting..." 
                  : "Please wait a couple of seconds, we are logging you in."}
              </p>
            </div>
            <Button 
              onClick={handleContinue}
              className="w-full"
              size="lg"
              disabled={!isAuthenticated}
            >
              {isAuthenticated ? "Continue" : "Logging in..."}
            </Button>
          </div>
        </div>
      )}
      
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center mb-8">
          <img 
            src={theme === 'dark' ? "/lovable-uploads/logo_mindnote_white.png" : "/lovable-uploads/321e5adc-687d-4ac5-904e-7be9d91e5340.png"}
            alt="MindNote"
            className="h-12 sm:h-16 w-auto object-contain"
          />
        </div>

        <div className="flex justify-center space-x-4">
          <Button
            variant={!showSignUp ? "default" : "outline"}
            onClick={() => setShowSignUp(false)}
          >
            Sign In
          </Button>
          <Button
            variant={showSignUp ? "default" : "outline"}
            onClick={() => setShowSignUp(true)}
          >
            Sign Up
          </Button>
        </div>

        <div className="mt-6">
          {showSignUp ? (
            <SignUpFormContainer />
          ) : (
            <SignInForm />
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
